import React from 'react';

import { Tag, Text } from '@chakra-ui/react';

import { createColumnHelper } from '@tanstack/react-table';

import { Order } from 'types/order';
import { Status } from 'types/items';
import { convertUTCToLocal } from 'utils/dateUtils';
import { capitalizeString } from 'components/primitives/StatCards/utils';
import { getSkuNameFromDetails } from '../../../utils/getSkuNameFromDetails';

export type SkuItemsById = Record<string, number | undefined>;

const col = createColumnHelper<Order>();

/** Single-SKU orders show that SKU's case count; multi-SKU orders show the sum. */
const caseAmount = (order: Order) => {
  const details = order?.details;
  const skuID = details?.length === 1 ? details[0]?.sku : '';

  if (skuID) {
    return details.find((d) => d.sku === skuID)?.casesCount || ' - ';
  }

  return details?.reduce((acc, d) => d?.casesCount + acc, 0);
};

export const buildOrderColumns = (skuItemsById: SkuItemsById) => [
  col.accessor('orderNumber', {
    header: 'Order No',
    minSize: 70,
    cell: (info) => info.getValue() || '-',
  }),
  col.accessor(
    (order) => {
      const details = order?.details;
      return details?.length > 1
        ? `2+ SKU's`
        : getSkuNameFromDetails(order?.skuDetails, details?.[0]?.sku ?? '');
    },
    { id: 'skuName', header: 'SKU name', minSize: 150 },
  ),
  col.accessor(
    (order) =>
      capitalizeString(order?.invoiced ? String(order.invoiced) : 'false'),
    {
      id: 'invoiced',
      header: 'Invoice',
      minSize: 90,
      meta: { sortField: 'invoiced' },
    },
  ),
  col.accessor((order) => convertUTCToLocal(order?.deliveryAt, true, true), {
    id: 'deliveryAt',
    header: 'Delivery Date',
    minSize: 150,
    meta: { sortField: 'deliveryAt' },
  }),
  col.accessor((order) => order?.site?.name || '-', {
    id: 'site',
    header: 'Site',
    minSize: 160,
    meta: { sortField: 'site.name' },
  }),
  col.accessor(
    (order) =>
      order?.adminCreator?.fname || order?.partnerCreator?.fname || '-',
    { id: 'createdBy', header: 'Created By', minSize: 110 },
  ),
  col.display({
    id: 'source',
    header: 'Source',
    minSize: 60,
    size: 60,
    meta: { truncate: false },
    cell: ({ row: { original: order } }) => (
      <Tag
        w="35px"
        colorScheme={
          order?.partnerCreator
            ? 'pink'
            : order?.adminCreator
            ? 'blue'
            : order?.portal
            ? 'orange'
            : order?.group
            ? 'pink'
            : 'blue'
        }>
        <Text fontWeight="bold">
          {order?.partnerCreator
            ? 'PP'
            : order?.adminCreator
            ? 'AD'
            : order?.portal
            ? 'CF'
            : order?.group
            ? 'PP'
            : 'AD'}
        </Text>
      </Tag>
    ),
  }),
  col.accessor((order) => order?.notes || '-', {
    id: 'notes',
    header: 'Notes',
    minSize: 200,
    size: 320,
  }),
  col.accessor((order) => caseAmount(order), {
    id: 'caseAmount',
    header: 'Case Amount',
    minSize: 100,
    meta: { align: 'center' },
  }),
  col.accessor((order) => Status[order?.status || 0], {
    id: 'status',
    header: 'Status',
    minSize: 110,
    meta: { align: 'center' },
  }),
  col.accessor(
    (order) =>
      order?.details?.reduce((skuAdded, orderDetails) => {
        const itemsQtyInCase = skuItemsById[orderDetails.sku] ?? 1;
        return skuAdded + orderDetails.casesCount * itemsQtyInCase;
      }, 0),
    {
      id: 'cupsCount',
      header: 'Cups Count',
      minSize: 90,
      meta: { align: 'center' },
    },
  ),
];
